import * as t from 'io-ts';
import {isString} from 'lodash';

import {Renderer, RendererOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {InboxModel} from '../../../models/api/inboxModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {Expression, ExpressionId, isExpression} from './expressionModel';
import {RExpressionRef, RTeammateIdOfRef} from './iotsCodecs';

export interface ExpressionRef extends t.TypeOf<typeof RExpressionRef> {}

export function isExpressionRef(value: any): value is ExpressionRef {
  return Boolean(value && isString(value.expression));
}

export function buildExpressionRef(expression: Expression): ExpressionRef {
  return {expression: expression.id};
}

/*
 * String of expression ref.
 */

export type WithExpressionRef<T> = T | ExpressionRef;
export type WithExpression<T> = T | Expression;

export type StringOrRef = WithExpressionRef<string>;

export function isStringOrRef(value: any): value is StringOrRef {
  return isString(value) || isExpressionRef(value);
}

export function toInlineString(val: StringOrRef | undefined) {
  return isExpressionRef(val) ? undefined : val;
}

/*
 * Teammate or expression ref.
 */

export type TeammateIdOrRef = t.TypeOf<typeof RTeammateIdOfRef>;

export type TeammateOrExpression = WithExpression<TeammateModel>;

interface ParsedTeammateIds {
  teammateIds: ReadonlyArray<TeammateModel['id']>;
  expressionIds: ReadonlyArray<ExpressionId>;
}

export function getTeammateOrExpressionId(value: TeammateOrExpression | undefined) {
  if (value === undefined) {
    return undefined;
  }

  return isExpression(value) ? buildExpressionRef(value) : value.id;
}

export function getTeammateOrExpressionKey(value: TeammateOrExpression) {
  return isExpression(value) ? `expression-${value.id}` : `teammate-${value.id}`;
}

export function parseTeammateIdOrRefs(teammateIdOrRefs?: ReadonlyArray<TeammateIdOrRef>): ParsedTeammateIds {
  if (!teammateIdOrRefs) {
    return {teammateIds: [], expressionIds: []};
  }

  return {
    teammateIds: teammateIdOrRefs.filter((id) => !isExpressionRef(id)) as ReadonlyArray<TeammateModel['id']>,
    expressionIds: teammateIdOrRefs
      .filter((ref) => isExpressionRef(ref))
      .map((ref) => (ref as ExpressionRef).expression),
  };
}
export function buildTeammateIdOrRefs(
  teammateIds: ReadonlyArray<TeammateModel['id']>,
  expressionIds: ReadonlyArray<ExpressionId>,
) {
  return [...expressionIds.map((id) => ({expression: id} as ExpressionRef)), ...teammateIds];
}

export function toInlineTeammateId(idOrRef: TeammateIdOrRef | undefined): TeammateModel['id'] | undefined {
  return isExpressionRef(idOrRef) ? undefined : idOrRef;
}

/*
 * Inbox.
 */

export type InboxIdOrRef = WithExpressionRef<InboxModel['id']>;

export type InboxOrExpression = WithExpression<InboxModel>;

interface ParsedInboxIds {
  inboxIds: ReadonlyArray<InboxModel['id']>;
  expressionIds: ReadonlyArray<ExpressionId>;
}

export function getInboxOrExpressionKey(value: InboxOrExpression | undefined) {
  if (!value) {
    return 'any_inbox';
  }

  return isExpression(value) ? `expression-${value.id}` : `inbox-${value.id}`;
}

export function compareInboxOrExpressionId(
  a: InboxOrExpression | undefined,
  b: InboxOrExpression | undefined,
) {
  if (isExpression(a) !== isExpression(b)) {
    return false;
  }

  if (a && b) {
    return a.id === b.id;
  }

  return a === undefined && b === undefined;
}

export function parseInboxIdOrRefs(inboxIdOrRefs?: ReadonlyArray<InboxIdOrRef>): ParsedInboxIds {
  if (!inboxIdOrRefs) {
    return {inboxIds: [], expressionIds: []};
  }

  return {
    inboxIds: inboxIdOrRefs.filter((id) => !isExpressionRef(id)) as ReadonlyArray<InboxModel['id']>,
    expressionIds: inboxIdOrRefs
      .filter((ref) => isExpressionRef(ref))
      .map((ref) => (ref as ExpressionRef).expression),
  };
}

export function buildInboxIdOrRefs(
  inboxIds: ReadonlyArray<InboxModel['id']>,
  expressionIds: ReadonlyArray<ExpressionId>,
) {
  return [...expressionIds.map((id) => ({expression: id} as ExpressionRef)), ...inboxIds];
}

/*
 * Helpers.
 */

export function resolveExpression(ref: any, expressions: ReadonlyArray<Expression>) {
  if (!isExpressionRef(ref)) {
    return undefined;
  }

  return expressions.find((expression) => expression.id === ref.expression);
}

type Id = number | string;

export function resolveValueOrExpression<T extends {id: Id}>(
  value: Id | ExpressionRef | undefined,
  values: ReadonlyArray<T>,
  expressions: ReadonlyArray<Expression>,
) {
  if (value === undefined) {
    return undefined;
  }

  return isExpressionRef(value)
    ? expressions.find((expression) => expression.id === value.expression)
    : values.find((v) => v.id === value);
}

export function renderValueOrExpression<T>(
  value: T | Expression | undefined,
  inlineRenderer: RendererOf<T>,
  expressionRenderer: RendererOf<Expression>,
  defaultRenderer?: Renderer,
) {
  if (isExpression(value)) {
    return expressionRenderer(value);
  }

  if (isDefined(value)) {
    return inlineRenderer(value);
  }

  return defaultRenderer || null;
}
